/**
 * janus-sip — high-level TypeScript wrapper around janus.js + janus.plugin.sip.
 *
 * Encapsulates the verbose janus.js callback API in a typed, event-emitter
 * facade. Same flow that proved working in test.html (echo + external mobile
 * call with 2-way audio).
 */

import type { PbxConfig, SipCredentials } from './api';
import { micConstraints, useAudio } from '@/store/audio';
import { deviceMonitor, type DeviceChangeEvent } from './device-monitor';

export type CallDirection = 'inbound' | 'outbound';
export type SipState =
  | 'idle'
  | 'registering'
  | 'registered'
  | 'register_failed'
  | 'calling'
  | 'ringing'
  | 'in_call'
  | 'hangup';

export interface CallInfo {
  callId: string;
  direction: CallDirection;
  peer: string;       // Remote number (caller for inbound, dialed for outbound)
  displayName?: string;
  startedAt: number;
  answeredAt?: number;
}

export type NetworkHealth = 'ok' | 'degraded' | 'reconnecting' | 'lost';

export interface JanusSipEvents {
  state: (state: SipState) => void;
  call: (call: CallInfo | null) => void;
  incoming: (call: CallInfo) => void;
  hangup: (reason: string) => void;
  registered: () => void;
  registration_failed: (code: number, reason: string) => void;
  remoteStream: (stream: MediaStream | null) => void;
  log: (line: string) => void;
  error: (err: Error) => void;
  /** Birleşik ağ sağlığı: WS bağlantı + ICE durumu beraber değerlendirilir. */
  health: (status: NetworkHealth) => void;
}

type Listener<K extends keyof JanusSipEvents> = JanusSipEvents[K];

export interface JanusSipConfig {
  janusWsUrl: string;
  iceServers: RTCIceServer[];
  /** When 'relay', browser forces TURN — bypasses host/srflx candidate
   *  selection that otherwise traps VPN/NAT agents in a half-open state
   *  where DTLS completes but SRTP silently drops. */
  iceTransportPolicy?: RTCIceTransportPolicy;
  sipCredentials: SipCredentials;
  pbx: { host: string; port: number; fqdn: string; transport: 'udp' | 'tcp' | 'tls' };
}

declare const Janus: any;

/**
 * Preferred audio codec order — PCMA/PCMU (G.711) önce, çünkü 3CX'in PSTN
 * trunk provider'ı sadece G.711 destekliyor. Opus advertise edersek 3CX
 * her çağrıda transcoding yapmak zorunda kalır (CPU + latency + kalite kaybı).
 * Native G.711 ile end-to-end aynı codec — sıfır transcoding.
 *
 * Sıralama Türkiye/EU için PCMA (A-law) first, sonra PCMU (US μ-law), fallback Opus.
 */
const PREFERRED_AUDIO_CODECS = ['pcma', 'pcmu', 'g722', 'opus', 'telephone-event'];

/**
 * Reorder the payload-type list on the m=audio line so preferred codecs
 * come first. Plus: PCMA varsa PCMU'yu tamamen drop ediyoruz, çünkü
 * browser sender (örn. answer'da) remote offer'daki PT sırasına bağlı kalıp
 * PCMU seçebiliyor → karşı tarafta PCMA decoder ile codec mismatch (sessizlik
 * veya gürültü). PCMU SDP'de hiç olmazsa sender PCMA'yı seçmek zorunda kalır.
 */
function reorderSdpAudioCodecs(sdp: string, preferred: string[]): string {
  const lines = sdp.split(/\r?\n/);
  const mIdx = lines.findIndex((l) => l.startsWith('m=audio'));
  if (mIdx === -1) return sdp;

  // Build codec name → payload-type map from a=rtpmap lines.
  const codecToPt = new Map<string, string>();
  for (const l of lines) {
    const m = /^a=rtpmap:(\d+)\s+([^/]+)\//.exec(l);
    if (m) codecToPt.set(m[2].toLowerCase(), m[1]);
  }

  // Drop PCMU if PCMA is present — prevents asymmetric codec selection where
  // sender uses μ-law and remote decoder expects A-law (or vice versa).
  const pcmaPt = codecToPt.get('pcma');
  const pcmuPt = codecToPt.get('pcmu');
  const dropPts = new Set<string>();
  if (pcmaPt && pcmuPt) dropPts.add(pcmuPt);

  // Current m=audio line: m=audio <port> <proto> <pt1> <pt2> ...
  const parts = lines[mIdx].split(/\s+/);
  if (parts.length < 4) return sdp;
  const head = parts.slice(0, 3);
  const existingPts = new Set(parts.slice(3).filter((pt) => !dropPts.has(pt)));

  const newOrder: string[] = [];
  for (const name of preferred) {
    const pt = codecToPt.get(name.toLowerCase());
    if (pt && existingPts.has(pt)) {
      newOrder.push(pt);
      existingPts.delete(pt);
    }
  }
  for (const pt of existingPts) newOrder.push(pt);

  lines[mIdx] = [...head, ...newOrder].join(' ');

  // Drop edilen PT'lerin a=rtpmap, a=fmtp, a=rtcp-fb satırlarını da SDP'den
  // sil — temizlik + bazı SIP stack'leri "tanımsız PT" varsa reject ediyor.
  if (dropPts.size > 0) {
    const cleaned = lines.filter((l) => {
      const r = /^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)\b/.exec(l);
      return !(r && dropPts.has(r[1]));
    });
    return cleaned.join('\r\n');
  }
  return lines.join('\r\n');
}

/**
 * Force preferred codec order on every audio RTCRtpTransceiver of the given pc.
 * Browser sender then chooses payload type by THIS priority, not by remote
 * SDP's order — fixes the case where 3CX offers PCMU-first and our answer's
 * reorder doesn't carry through to the sender.
 */
function forceCodecPreferences(pc: RTCPeerConnection | undefined): void {
  if (!pc) return;
  const TX = (window as any).RTCRtpTransceiver;
  if (!TX || !('setCodecPreferences' in TX.prototype)) return;
  const caps = (RTCRtpSender as any).getCapabilities?.('audio');
  if (!caps?.codecs) return;
  const pri = (c: { mimeType: string }) => {
    const n = c.mimeType.toLowerCase();
    if (n.endsWith('/pcma')) return 0;
    if (n.endsWith('/pcmu')) return 1;
    if (n.endsWith('/g722')) return 2;
    if (n.endsWith('/opus')) return 3;
    if (n.endsWith('/telephone-event')) return 9;
    return 5;
  };
  // PCMA varsa PCMU'yu CAPS'ten KOMPLE çıkar — browser sender'ı PCMU
  // seçemez. SDP munging yetmedi (Chrome cached negotiation kullanıyor),
  // caps filter en sert çözüm.
  const hasPcma = caps.codecs.some((c: any) =>
    c.mimeType?.toLowerCase().endsWith('/pcma'));
  const filteredCaps = hasPcma
    ? caps.codecs.filter((c: any) => !c.mimeType?.toLowerCase().endsWith('/pcmu'))
    : caps.codecs;
  const ordered = [...filteredCaps].sort((a: any, b: any) => pri(a) - pri(b));
  let count = 0;
  for (const tx of pc.getTransceivers()) {
    const k = tx.sender?.track?.kind || tx.receiver?.track?.kind;
    if (k !== 'audio') continue;
    try {
      tx.setCodecPreferences(ordered);
      count++;
    } catch (e) {
      console.warn('[codec] setCodecPreferences failed', e);
    }
  }
  if (count > 0) {
    console.log('[codec] forced preferences on', count, 'transceiver(s); pcma-first, pcmu filtered:', hasPcma);
  }
}

function describeMAudio(sdp: string): string {
  const m = sdp.split(/\r?\n/).find((l) => l.startsWith('m=audio'));
  if (!m) return '';
  // Resolve PT names from rtpmap
  const ptNames = new Map<string, string>();
  for (const l of sdp.split(/\r?\n/)) {
    const r = /^a=rtpmap:(\d+)\s+([^/]+)\/(\d+)/.exec(l);
    if (r) ptNames.set(r[1], `${r[2]}/${r[3]}`);
  }
  const parts = m.split(/\s+/);
  const pts = parts.slice(3).map((pt) => ptNames.get(pt) ?? pt);
  return pts.join(', ');
}

export class JanusSip {
  private janus: any = null;
  private sipHandle: any = null;
  private remoteStream: MediaStream | null = null;
  private currentCall: CallInfo | null = null;
  private pendingIncomingJsep: any = null;
  private listeners = new Map<keyof JanusSipEvents, Set<Function>>();
  private state: SipState = 'idle';
  private deviceMonitorUnsub?: () => void;
  private audioPrefsUnsub?: () => void;
  private lastSwappedMicId: string | null = null;
  // Resilience state
  private intentionalDestroy = false;
  /** True while the browser reports offline — gates all reconnect/register
   *  attempts so we don't spam timers while the network is genuinely down. */
  private reconnectPaused = false;
  /** True while WE are intentionally tearing down the old Janus to reconnect.
   *  Janus.destroy() tetiklediği `destroyed` callback'i, kendi teardown'umuzu
   *  YENİ bir reconnect olarak yorumlayıp sonsuz döngü yaratıyordu (bir kez
   *  reconnect başlayınca çalışan bağlantıyı 2s sonra yıkıp duruyordu). Bu
   *  bayrak o callback'i bizim teardown'umuzda susturur. */
  private intentionalReconnectTeardown = false;
  private wsReconnectAttempt = 0;
  private wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private iceDisconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private boundPc: RTCPeerConnection | null = null;
  private health: NetworkHealth = 'ok';
  // SIP REGISTER staleness recovery (separate from WS reconnect)
  private sipReregisterAttempt = 0;
  private sipReregisterTimer: ReturnType<typeof setTimeout> | null = null;
  private lastForceRegisterAt = 0;
  /** Sofia-SIP REGISTER watchdog — if 'registering' state lingers past this
   *  without a 'registered'/'registration_failed' event, the SIP stack is
   *  stuck (REGISTER lost, ACK lost, 3CX silently dropped). Trigger full
   *  WS reconnect to recover. */
  private registeringWatchdog: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly config: JanusSipConfig) {}

  // ─── Event emitter ───────────────────────────────────────────────
  on<K extends keyof JanusSipEvents>(ev: K, fn: Listener<K>): () => void {
    if (!this.listeners.has(ev)) this.listeners.set(ev, new Set());
    this.listeners.get(ev)!.add(fn);
    return () => this.listeners.get(ev)?.delete(fn);
  }
  private emit<K extends keyof JanusSipEvents>(ev: K, ...args: Parameters<Listener<K>>) {
    this.listeners.get(ev)?.forEach((fn) => {
      try { (fn as any)(...args); } catch (e) { console.error('listener error', e); }
    });
  }

  private setState(s: SipState) {
    if (this.state === s) return;
    this.state = s;
    this.emit('state', s);
  }

  // ─── Lifecycle ───────────────────────────────────────────────────
  async init(): Promise<void> {
    await new Promise<void>((resolve) => Janus.init({
      debug: 'warn',
      dependencies: Janus.useDefaultDependencies(),
      callback: resolve,
    }));
    // Start device monitor and react to physical device changes mid-call.
    await deviceMonitor.start();
    this.deviceMonitorUnsub = deviceMonitor.subscribe((ev) => this.onDeviceChange(ev));
    // Also react when the user manually changes mic in the Audio Settings panel.
    this.audioPrefsUnsub = useAudio.subscribe((s, prev) => {
      if (s.micDeviceId !== prev.micDeviceId) void this.swapMicTrack('user changed mic');
    });
  }

  /** Debug: ne tür track'ler gönderiliyor? Mic problemini teşhis için. */
  private logSenderTracks(reason: string): void {
    const pc: RTCPeerConnection | undefined = this.sipHandle?.webrtcStuff?.pc;
    if (!pc) { console.warn('[tracks] no pc', reason); return; }
    const senders = pc.getSenders();
    if (senders.length === 0) { console.warn('[tracks] no senders', reason); return; }
    senders.forEach((s, i) => {
      const t = s.track;
      if (!t) {
        console.warn(`[tracks] sender#${i} HAS NO TRACK — karşı tarafa ses gitmiyor!`, reason);
      } else {
        console.log(`[tracks] sender#${i} ${t.kind} label="${t.label}" enabled=${t.enabled} muted=${t.muted} readyState=${t.readyState}`, reason);
      }
    });
  }

  /** Force-stop every audio track on every sender + receiver. Called from
   *  oncleanup, hangup paths, and destroy. Without this the mic LED stays on
   *  and tracks leak across calls. */
  private stopAllMediaTracks(reason: string): void {
    const pc: RTCPeerConnection | undefined = this.sipHandle?.webrtcStuff?.pc;
    if (!pc) return;
    let stopped = 0;
    pc.getSenders().forEach((s) => {
      if (s.track) { s.track.stop(); stopped++; }
    });
    pc.getReceivers().forEach((r) => {
      if (r.track) { r.track.stop(); stopped++; }
    });
    if (stopped > 0) this.emit('log', `stopped ${stopped} media track(s) [${reason}]`);
  }

  /** Mid-call mic swap — get fresh stream with current constraints, replace
   *  the audio track on the active sender, stop the old track. */
  async swapMicTrack(reason: string): Promise<void> {
    const pc: RTCPeerConnection | undefined = this.sipHandle?.webrtcStuff?.pc;
    if (!pc) return;
    const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
    if (!sender) return;
    const prefs = useAudio.getState();
    // De-dup: if we're already on this device, skip
    if (this.lastSwappedMicId === prefs.micDeviceId && sender.track) return;

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints() });
      const newTrack = newStream.getAudioTracks()[0];
      if (!newTrack) return;
      const oldTrack = sender.track;
      await sender.replaceTrack(newTrack);
      if (oldTrack) oldTrack.stop();
      this.lastSwappedMicId = prefs.micDeviceId;
      console.log('[mic-swap] replaced mic track:', reason, '→', newTrack.label || 'default');
      this.emit('log', `mic swapped: ${reason}`);
    } catch (e) {
      console.warn('[mic-swap] failed', e);
      this.emit('error', new Error('mic_swap_failed: ' + (e as Error).message));
    }
  }

  private onDeviceChange(ev: DeviceChangeEvent): void {
    // Only act mid-call. If idle, the next call will pick up new constraints naturally.
    if (this.state !== 'in_call' && this.state !== 'ringing' && this.state !== 'calling') return;
    if (ev.selectedMicLost) {
      void this.swapMicTrack('selected mic disappeared, falling back to default');
    } else if (ev.removed.length > 0 || ev.added.length > 0) {
      // System default may have changed (e.g. headset just plugged in becomes default
      // on Windows). If user is on 'default', re-acquire to follow the OS default.
      if (useAudio.getState().micDeviceId === 'default') {
        void this.swapMicTrack('device list changed, refreshing default mic');
      }
    }
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.janus = new Janus({
        server: this.config.janusWsUrl,
        iceServers: this.config.iceServers,
        iceTransportPolicy: this.config.iceTransportPolicy ?? 'all',
        success: () => {
          this.attachSipPlugin().then(resolve).catch(reject);
        },
        error: (e: any) => {
          this.emit('error', new Error('Janus connect failed: ' + e));
          // İlk connect başarısızsa bile resolve etmeden retry başlat
          if (!this.intentionalDestroy && !this.reconnectPaused) this.scheduleReconnect();
          reject(new Error('janus_connect_failed'));
        },
        destroyed: () => {
          this.emit('log', 'janus destroyed');
          // BEKLENMEDIK destroy = WS koptu → reconnect dene. AMA bizim reconnect
          // teardown'umuzun tetiklediği destroy'u SAYMA — yoksa her reconnect
          // kendini besleyen sonsuz döngüye girer (çalışan bağlantıyı 2s sonra yıkar).
          if (this.intentionalDestroy || this.reconnectPaused || this.intentionalReconnectTeardown) return;
          this.scheduleReconnect();
        },
      });
    });
  }

  private attachSipPlugin(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.janus.attach({
        plugin: 'janus.plugin.sip',
        success: (h: any) => {
          this.sipHandle = h;
          this.emit('log', 'SIP plugin attached');
          resolve();
        },
        error: (e: any) => reject(new Error('attach_failed: ' + e)),
        onmessage: this.onMessage.bind(this),
        onremotetrack: this.onRemoteTrack.bind(this),
        webrtcState: (on: boolean, reason?: string) => {
          this.emit('log', `webrtcState=${on}${reason ? ' (' + reason + ')' : ''}`);
          if (on) this.bindPcHealthListeners();
        },
        oncleanup: () => {
          this.emit('log', 'sip cleanup');
          this.stopAllMediaTracks('oncleanup');
          this.boundPc = null;
          this.remoteStream = null;
          this.emit('remoteStream', null);
          this.lastSwappedMicId = null;
        },
      });
    });
  }

  /** Birleşik health setter — duplicate emit'leri filtreler. */
  private setHealth(h: NetworkHealth): void {
    if (this.health === h) return;
    this.health = h;
    this.emit('health', h);
  }

  /** PeerConnection ICE state'lerini dinle. Idempotent — aynı pc'ye 2 kez bind etmez. */
  private bindPcHealthListeners(): void {
    const pc: RTCPeerConnection | undefined = this.sipHandle?.webrtcStuff?.pc;
    if (!pc || pc === this.boundPc) return;
    this.boundPc = pc;

    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      this.emit('log', `iceConnectionState=${s}`);

      if (s === 'connected' || s === 'completed') {
        this.setHealth('ok');
        if (this.iceDisconnectTimer) {
          clearTimeout(this.iceDisconnectTimer);
          this.iceDisconnectTimer = null;
        }
      } else if (s === 'disconnected') {
        // Brief drops often self-recover within 1-3s. 3s watchdog before ICE restart.
        this.setHealth('degraded');
        if (!this.iceDisconnectTimer) {
          this.iceDisconnectTimer = setTimeout(() => {
            this.iceDisconnectTimer = null;
            if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
              this.emit('log', '[ice-restart] watchdog firing after 3s disconnect');
              void this.triggerIceRestart();
            }
          }, 3000);
        }
      } else if (s === 'failed') {
        // 'failed' is terminal in standard WebRTC — only ICE restart can recover.
        void this.triggerIceRestart();
      } else if (s === 'closed') {
        this.boundPc = null;
      }
    };
  }

  /** Re-INVITE with ICE restart — recovers from brief network changes (wifi→4G,
   *  NAT timeout, ISP reroute) without dropping the call. */
  async triggerIceRestart(): Promise<void> {
    const pc: RTCPeerConnection | undefined = this.sipHandle?.webrtcStuff?.pc;
    if (!pc || !this.sipHandle) return;
    if (this.state !== 'in_call' && this.state !== 'ringing' && this.state !== 'calling') return;
    this.setHealth('reconnecting');
    this.emit('log', '[ice-restart] creating offer with iceRestart=true');

    return new Promise((resolve) => {
      this.sipHandle.createOffer({
        iceRestart: true,
        tracks: [{ type: 'audio', capture: micConstraints(), recv: true }],
        customizeSdp: (jsep: any) => {
          forceCodecPreferences(this.sipHandle?.webrtcStuff?.pc);
          jsep.sdp = reorderSdpAudioCodecs(jsep.sdp, PREFERRED_AUDIO_CODECS);
        },
        success: (jsep: any) => {
          this.sipHandle.send({ message: { request: 'update' }, jsep });
          this.emit('log', '[ice-restart] update INVITE sent');
          // Health döner success/fail'i pc.oniceconnectionstatechange'den geri gelir.
          // Eğer 10s içinde 'connected' olmazsa 'lost' işaretle.
          setTimeout(() => {
            if (this.health === 'reconnecting') this.setHealth('lost');
          }, 10_000);
          resolve();
        },
        error: (e: any) => {
          this.emit('log', '[ice-restart] failed: ' + e);
          this.setHealth('lost');
          resolve();
        },
      });
    });
  }

  /** Janus session yeniden bağlanma — WS koparsa exponential backoff. */
  private scheduleReconnect(): void {
    if (this.intentionalDestroy || this.reconnectPaused) return;
    if (this.wsReconnectTimer) return;
    const delays = [1000, 2000, 4000, 8000, 16000, 30000, 60000];
    const idx = Math.min(this.wsReconnectAttempt, delays.length - 1);
    const delay = delays[idx] + Math.floor(Math.random() * 500);
    this.wsReconnectAttempt += 1;
    this.setHealth('reconnecting');
    this.emit('log', `[ws-reconnect] attempt ${this.wsReconnectAttempt} in ${delay}ms`);
    this.wsReconnectTimer = setTimeout(async () => {
      this.wsReconnectTimer = null;
      const startedAt = Date.now();
      try {
        // Tear down any zombie handles — kendi destroy'umuz reconnect döngüsü
        // başlatmasın (notifyDestroyed:false + flag, çift güvenlik).
        this.intentionalReconnectTeardown = true;
        try { this.janus?.destroy({ notifyDestroyed: false }); } catch { /* */ }
        this.sipHandle = null;
        this.janus = null;
        await this.connect();
        this.intentionalReconnectTeardown = false;
        this.register();
        this.emit('log', '[ws-reconnect] succeeded');
        // Health: pc bağlı değil yetkili olarak 'ok' demeyelim — register başarılı olduktan
        // sonra normal akışta health ICE listener'dan gelir. Şimdilik recovered baseline:
        this.setHealth('ok');
        // Only reset backoff counter if the connection has been alive for
        // SUSTAIN_MS — otherwise WS that immediately dies after handshake
        // (NGINX upstream flapping, Janus crash-looping) defeats the
        // exponential backoff and we hammer reconnect every 1s.
        const SUSTAIN_MS = 10_000;
        setTimeout(() => {
          if (this.janus && this.sipHandle && Date.now() - startedAt >= SUSTAIN_MS) {
            this.wsReconnectAttempt = 0;
            this.emit('log', '[ws-reconnect] connection sustained 10s, backoff reset');
          }
        }, SUSTAIN_MS);
      } catch (e) {
        this.intentionalReconnectTeardown = false;
        this.emit('log', '[ws-reconnect] failed, retrying');
        this.scheduleReconnect();
      }
    }, delay);
  }

  register(): void {
    if (!this.sipHandle) throw new Error('not_connected');
    this.setState('registering');
    const { username, auth_id, secret } = this.config.sipCredentials;
    this.sipHandle.send({
      message: {
        request: 'register',
        username: `sip:${username}@${this.config.pbx.fqdn}`,
        authuser: auth_id,
        secret,
        proxy: `sip:${this.config.pbx.host}:${this.config.pbx.port};transport=${this.config.pbx.transport}`,
        force_udp: this.config.pbx.transport === 'udp',
        sips: false,
      },
    });
    // Watchdog: REGISTER must produce 'registered' or 'registration_failed'
    // within 10s. Otherwise Sofia-SIP is stuck (lost ACK, dead UDP socket,
    // 3CX silently dropped) and only a full Janus WS reconnect recovers.
    // Most real-world REGISTER round trips complete in <500ms; 10s is well
    // past any normal SIP timer (T1=500ms, retransmits at 1,2,4,8s).
    if (this.registeringWatchdog) clearTimeout(this.registeringWatchdog);
    this.registeringWatchdog = setTimeout(() => {
      this.registeringWatchdog = null;
      if (this.state !== 'registering') return;
      console.warn('[sip] REGISTER watchdog fired (10s) — Sofia-SIP appears stuck. Tearing down for full reconnect');
      this.setState('register_failed');
      this.emit('registration_failed', 0, 'register_timeout');
      try { this.janus?.destroy(); } catch { /* */ }
      this.sipHandle = null;
      this.janus = null;
      this.scheduleReconnect();
    }, 10000);
  }

  unregister(): void {
    try { this.sipHandle?.send({ message: { request: 'unregister' } }); } catch { /* */ }
  }

  /**
   * Bypass all backoff timers and reconnect/re-register RIGHT NOW. Called
   * by useSipHealthRecovery when 3CX's xapi flips from unreachable back to
   * reachable — without this we'd wait for the exponential WS backoff
   * (up to 30s) even though the PBX is already serving requests again.
   *
   * Resets all retry counters and cancels pending timers so cooldown
   * guards don't kick in.
   */
  triggerImmediateReconnect(): void {
    if (this.intentionalDestroy) return;
    if (this.state === 'in_call' || this.state === 'calling' || this.state === 'ringing') {
      console.log('[immediate-reconnect] in-call, skipping');
      return;
    }
    console.log('[immediate-reconnect] requested — resetting all backoff state');
    // Cancel any scheduled retry timers
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
    if (this.sipReregisterTimer) {
      clearTimeout(this.sipReregisterTimer);
      this.sipReregisterTimer = null;
    }
    // Reset counters so the next attempt isn't penalized for previous failures
    this.wsReconnectAttempt = 0;
    this.sipReregisterAttempt = 0;
    this.lastForceRegisterAt = 0;

    // Already mid-register? Let the watchdog handle it; double-calling
    // register() would just stack REGISTER messages.
    if (this.state === 'registering') {
      console.log('[immediate-reconnect] already registering, watchdog will handle');
      return;
    }

    // Fast path: sipHandle alive → just re-register without tearing down WS
    if (this.sipHandle) {
      console.log('[immediate-reconnect] sipHandle alive, registering directly');
      try { this.register(); } catch (e) {
        console.warn('[immediate-reconnect] register() threw, full reconnect:', e);
        this.fullReconnectNow();
      }
      return;
    }

    // No sipHandle → need full Janus reconnect, but immediately (no backoff)
    this.fullReconnectNow();
  }

  /**
   * Halt all reconnect/re-register timers. Called from the browser
   * `offline` event — when the network is down there's no point hammering
   * a connect attempt every 1-30s; we just spam the console and waste CPU.
   * `resumeReconnect()` unpauses and immediately tries once.
   */
  pauseReconnect(): void {
    if (this.reconnectPaused) return;
    console.log('[ws-reconnect] paused (network offline)');
    this.reconnectPaused = true;
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
    if (this.sipReregisterTimer) {
      clearTimeout(this.sipReregisterTimer);
      this.sipReregisterTimer = null;
    }
    if (this.registeringWatchdog) {
      clearTimeout(this.registeringWatchdog);
      this.registeringWatchdog = null;
    }
    this.setHealth('lost');
  }

  /**
   * Unpause reconnect after `pauseReconnect()`. Resets attempt counters
   * since the network was just restored — first attempt should be immediate,
   * not the exponential delay accumulated during the offline window.
   */
  resumeReconnect(): void {
    if (!this.reconnectPaused) return;
    console.log('[ws-reconnect] resumed (network online), triggering immediate attempt');
    this.reconnectPaused = false;
    this.wsReconnectAttempt = 0;
    this.sipReregisterAttempt = 0;
    this.lastForceRegisterAt = 0;
    if (this.sipHandle && this.janus) {
      // Connection survived → just re-register to validate
      try { this.register(); } catch (e) {
        console.warn('[ws-reconnect] resume register() threw, full reconnect:', e);
        this.fullReconnectNow();
      }
    } else {
      this.fullReconnectNow();
    }
  }

  /** Internal helper — destroys current Janus session and reconnects without
   *  any backoff delay. Used by triggerImmediateReconnect. */
  private fullReconnectNow(): void {
    void (async () => {
      try {
        this.intentionalReconnectTeardown = true;
        try { this.janus?.destroy({ notifyDestroyed: false }); } catch { /* */ }
        this.sipHandle = null;
        this.janus = null;
        await this.connect();
        this.intentionalReconnectTeardown = false;
        this.register();
        console.log('[immediate-reconnect] full reconnect succeeded');
      } catch (e) {
        this.intentionalReconnectTeardown = false;
        console.warn('[immediate-reconnect] full reconnect failed, falling back to scheduled retry:', e);
        this.scheduleReconnect();
      }
    })();
  }

  /**
   * Force a fresh REGISTER without waiting for TTL renew. Used by:
   *   - navigator 'online' event (network came back)
   *   - Page Visibility 'visible' event (tab refocused)
   *   - backend health probe saying 3CX has us as unregistered
   * Safe to call any time — if there's no sipHandle, becomes a no-op until
   * WS reconnects.
   */
  forceRegister(): void {
    if (!this.sipHandle) {
      console.log('[sip-reregister] no sipHandle, skipping forceRegister');
      return;
    }
    if (this.state === 'in_call' || this.state === 'calling' || this.state === 'ringing') {
      console.log('[sip-reregister] in-call, skipping forceRegister to avoid drop');
      return;
    }
    // Cooldown — useSipHealthRecovery polls every 30s and can call this
    // repeatedly during a sustained outage; without throttle we'd spam
    // REGISTER messages at 3CX and never give Sofia-SIP a chance to settle.
    const now = Date.now();
    const sinceLast = now - this.lastForceRegisterAt;
    if (sinceLast < 5000) {
      console.log(`[sip-reregister] cooldown active (${sinceLast}ms since last), skipping`);
      return;
    }
    // Also skip if we're mid-register — let it finish or hit the watchdog
    if (this.state === 'registering') {
      console.log('[sip-reregister] already registering, skipping forceRegister');
      return;
    }
    this.lastForceRegisterAt = now;
    console.log('[sip-reregister] forceRegister — clearing backoff and registering');
    this.sipReregisterAttempt = 0;
    if (this.sipReregisterTimer) {
      clearTimeout(this.sipReregisterTimer);
      this.sipReregisterTimer = null;
    }
    try { this.register(); } catch (e) {
      console.warn('[sip-reregister] register() threw:', e);
      this.scheduleSipReregister();
    }
  }

  /**
   * Schedule a SIP re-REGISTER with exponential backoff. Called when 3CX
   * unregisters us or REGISTER renewal fails. WS connection is assumed alive
   * (otherwise scheduleReconnect handles it). Backoff: 2s, 4s, 8s, 16s, 30s
   * (capped). Reset on successful register.
   */
  private scheduleSipReregister(): void {
    if (this.intentionalDestroy || this.reconnectPaused) return;
    if (this.sipReregisterTimer) return; // already scheduled
    const delay = Math.min(2000 * Math.pow(2, this.sipReregisterAttempt), 30000);
    this.sipReregisterAttempt++;
    console.log(`[sip-reregister] attempt #${this.sipReregisterAttempt} in ${delay}ms`);
    this.sipReregisterTimer = setTimeout(() => {
      this.sipReregisterTimer = null;
      if (!this.sipHandle) {
        // WS died meanwhile — let scheduleReconnect handle it
        return;
      }
      try {
        this.register();
      } catch (e) {
        console.warn('[sip-reregister] retry register() threw:', e);
        this.scheduleSipReregister();
      }
    }, delay);
  }

  /**
   * Place an outbound call.
   * IMPORTANT: URI uses LAN IP, not FQDN — Sofia-SIP bypasses /etc/hosts and
   * would route to the public IP otherwise (hairpin NAT problem). See
   * docs/architecture.md.
   */
  call(target: string): void {
    if (!this.sipHandle) throw new Error('not_connected');
    if (this.state === 'in_call' || this.state === 'calling') {
      throw new Error('busy');
    }
    const uri = `sip:${target}@${this.config.pbx.host}`;
    this.setState('calling');
    this.emit('log', `→ calling ${uri}`);
    this.sipHandle.createOffer({
      tracks: [{ type: 'audio', capture: micConstraints(), recv: true }],
      customizeSdp: (jsep: any) => {
        // Force sender preference BEFORE munging the SDP — runs after tracks
        // are added to the pc but before setLocalDescription.
        forceCodecPreferences(this.sipHandle?.webrtcStuff?.pc);
        const before = describeMAudio(jsep.sdp);
        jsep.sdp = reorderSdpAudioCodecs(jsep.sdp, PREFERRED_AUDIO_CODECS);
        console.log('[sdp] offer codecs reordered:', before, '→', describeMAudio(jsep.sdp));
        this.logSenderTracks('after createOffer customizeSdp');
      },
      success: (jsep: any) => {
        this.sipHandle.send({
          message: { request: 'call', uri },
          jsep,
        });
        this.currentCall = {
          callId: 'pending',
          direction: 'outbound',
          peer: target,
          startedAt: Date.now(),
        };
        this.emit('call', this.currentCall);
      },
      error: (e: any) => {
        this.emit('error', new Error('createOffer failed: ' + e));
        this.setState('idle');
      },
    });
  }

  accept(): void {
    console.log('[janus] accept() — handle=%s call=%s dir=%s jsep=%s',
      this.sipHandle ? 'set' : 'NULL',
      this.currentCall ? 'set' : 'NULL',
      this.currentCall?.direction,
      this.pendingIncomingJsep ? 'present' : 'MISSING');
    if (!this.sipHandle || !this.currentCall || this.currentCall.direction !== 'inbound') {
      this.emit('error', new Error(`accept guard failed (handle=${!!this.sipHandle} call=${!!this.currentCall} dir=${this.currentCall?.direction})`));
      return;
    }
    const jsep = this.pendingIncomingJsep;
    this.pendingIncomingJsep = null;

    if (jsep) {
      // ── Normal akış: INVITE SDP offer içeriyordu → createAnswer ──
      this.sipHandle.createAnswer({
        jsep,
        tracks: [{ type: 'audio', capture: micConstraints(), recv: true }],
        customizeSdp: (answerJsep: any) => {
          forceCodecPreferences(this.sipHandle?.webrtcStuff?.pc);
          const before = describeMAudio(answerJsep.sdp);
          answerJsep.sdp = reorderSdpAudioCodecs(answerJsep.sdp, PREFERRED_AUDIO_CODECS);
          console.log('[sdp] answer codecs reordered:', before, '→', describeMAudio(answerJsep.sdp));
          this.logSenderTracks('after createAnswer customizeSdp');
        },
        success: (answer: any) => {
          this.sipHandle.send({ message: { request: 'accept' }, jsep: answer });
          this.setState('in_call');
          if (this.currentCall) {
            this.currentCall.answeredAt = Date.now();
            this.emit('call', this.currentCall);
          }
        },
        error: (e: any) => this.emit('error', new Error('createAnswer: ' + e)),
      });
      return;
    }

    // ── Offerless INVITE (3CX KUYRUK çağrıları böyle geliyor) ──
    // INVITE'da SDP yoktu → BİZ offer üretiriz, 3CX answer'ı 'accepted'
    // event'inde döner ve handleRemoteJsep ile uygulanır (mesaj handler'ı).
    console.log('[janus] offerless INVITE — createOffer ile accept ediliyor');
    this.sipHandle.createOffer({
      tracks: [{ type: 'audio', capture: micConstraints(), recv: true }],
      customizeSdp: (offerJsep: any) => {
        forceCodecPreferences(this.sipHandle?.webrtcStuff?.pc);
        const before = describeMAudio(offerJsep.sdp);
        offerJsep.sdp = reorderSdpAudioCodecs(offerJsep.sdp, PREFERRED_AUDIO_CODECS);
        console.log('[sdp] offerless accept offer reordered:', before, '→', describeMAudio(offerJsep.sdp));
        this.logSenderTracks('after offerless createOffer customizeSdp');
      },
      success: (offer: any) => {
        this.sipHandle.send({ message: { request: 'accept' }, jsep: offer });
        // in_call optimistik set — accept gönderildi; remote answer 'accepted'
        // event'inde handleRemoteJsep ile uygulanacak (media kurulur).
        this.setState('in_call');
        if (this.currentCall) {
          this.currentCall.answeredAt = Date.now();
          this.emit('call', this.currentCall);
        }
      },
      error: (e: any) => this.emit('error', new Error('offerless createOffer: ' + e)),
    });
  }

  reject(): void {
    if (!this.sipHandle) return;
    try {
      this.sipHandle.send({ message: { request: 'decline', code: 486 } });
    } catch { /* */ }
    this.pendingIncomingJsep = null;
    this.currentCall = null;
    this.emit('call', null);
    this.setState('registered');
  }

  hangup(): void {
    try { this.sipHandle?.send({ message: { request: 'hangup' } }); } catch { /* */ }
  }

  hold(direction: 'sendonly' | 'recvonly' | 'inactive' = 'sendonly'): void {
    this.sipHandle?.send({ message: { request: 'hold', direction } });
  }

  unhold(): void {
    this.sipHandle?.send({ message: { request: 'unhold' } });
  }

  /** Toggle local mic on/off (client side, no SIP traffic). */
  setMute(mute: boolean): void {
    const pc: RTCPeerConnection | undefined = this.sipHandle?.webrtcStuff?.pc;
    if (!pc) return;
    pc.getSenders().forEach((s) => {
      if (s.track && s.track.kind === 'audio') s.track.enabled = !mute;
    });
  }

  /** Blind transfer to a target. */
  transferBlind(target: string): void {
    const refer = `sip:${target}@${this.config.pbx.host}`;
    this.sipHandle?.send({ message: { request: 'transfer', uri: refer } });
  }

  /** Send DTMF digit via SIP INFO. */
  dtmf(digit: string): void {
    this.sipHandle?.send({ message: { request: 'dtmf_info', digit } });
  }

  /** Dial a 3CX queue feature code (login/logout/pause/resume). */
  queueCode(code: string): void {
    this.call(code);
  }

  async getStats(): Promise<{
    iceState: string;
    bytesSent: number;
    bytesReceived: number;
    codecOut: string;
    codecIn: string;
    packetLossPct: number;
    jitterMs: number;
    rttMs: number;
  } | null> {
    const pc: RTCPeerConnection | undefined = this.sipHandle?.webrtcStuff?.pc;
    if (!pc) return null;
    let iceState = 'unknown';
    let bytesSent = 0;
    let bytesReceived = 0;
    let codecOut = 'unknown';
    let codecIn = 'unknown';
    let packetLossPct = 0;
    let jitterMs = 0;
    let rttMs = 0;

    const stats = await pc.getStats();
    // First pass: collect codec lookup table
    const codecMap = new Map<string, string>();
    stats.forEach((s: any) => {
      if (s.type === 'codec') {
        const ch = s.channels ?? 1;
        codecMap.set(s.id, `${s.mimeType?.replace('audio/', '') ?? '?'}/${s.clockRate ?? '?'}/${ch}`);
      }
    });
    // Second pass: resolve everything
    stats.forEach((s: any) => {
      if (s.type === 'transport') iceState = s.iceState || s.dtlsState || iceState;
      if (s.type === 'outbound-rtp' && s.kind === 'audio') {
        bytesSent = s.bytesSent || 0;
        codecOut = codecMap.get(s.codecId) ?? codecOut;
      }
      if (s.type === 'inbound-rtp' && s.kind === 'audio') {
        bytesReceived = s.bytesReceived || 0;
        codecIn = codecMap.get(s.codecId) ?? codecIn;
        jitterMs = (s.jitter ?? 0) * 1000;
        const recv = s.packetsReceived ?? 0;
        const lost = s.packetsLost ?? 0;
        const total = recv + lost;
        packetLossPct = total > 0 ? (lost / total) * 100 : 0;
      }
      if (s.type === 'candidate-pair' && (s.state === 'succeeded' || s.nominated)) {
        if (typeof s.currentRoundTripTime === 'number') {
          rttMs = s.currentRoundTripTime * 1000;
        }
      }
    });
    return { iceState, bytesSent, bytesReceived, codecOut, codecIn, packetLossPct, jitterMs, rttMs };
  }

  destroy(): void {
    this.intentionalDestroy = true;
    if (this.wsReconnectTimer) { clearTimeout(this.wsReconnectTimer); this.wsReconnectTimer = null; }
    if (this.iceDisconnectTimer) { clearTimeout(this.iceDisconnectTimer); this.iceDisconnectTimer = null; }
    // Stop tracks BEFORE tearing down PC so the OS releases the mic immediately.
    this.stopAllMediaTracks('destroy');
    try { this.unregister(); } catch { /* */ }
    try { this.janus?.destroy(); } catch { /* */ }
    this.deviceMonitorUnsub?.();
    this.audioPrefsUnsub?.();
    this.deviceMonitorUnsub = undefined;
    this.audioPrefsUnsub = undefined;
    this.janus = null;
    this.sipHandle = null;
    this.currentCall = null;
    this.remoteStream = null;
    this.pendingIncomingJsep = null;
    this.lastSwappedMicId = null;
    this.boundPc = null;
    this.listeners.clear();
  }

  // ─── Message dispatch ────────────────────────────────────────────
  private onMessage(msg: any, jsep: any) {
    const r = msg.result || {};
    this.emit('log', `event: ${r.event || 'message'}`);

    const inCall = this.state === 'in_call' || this.state === 'ringing' || this.state === 'calling';

    switch (r.event) {
      case 'registered':
        this.sipReregisterAttempt = 0;
        if (this.sipReregisterTimer) {
          clearTimeout(this.sipReregisterTimer);
          this.sipReregisterTimer = null;
        }
        if (this.registeringWatchdog) {
          clearTimeout(this.registeringWatchdog);
          this.registeringWatchdog = null;
        }
        // ÇAĞRI SIRASINDA gelen TTL re-register (Sofia ~60s'de yeniler) in_call
        // state'ini EZMESİN — yoksa softphone barları/caller kartı kapanır ama
        // RTP/görüşme sürer (agent kiminle konuştuğunu göremez). Sadece çağrı
        // yokken 'registered'a geç.
        if (!inCall) this.setState('registered');
        this.emit('registered');
        break;
      case 'registration_failed':
        if (this.registeringWatchdog) {
          clearTimeout(this.registeringWatchdog);
          this.registeringWatchdog = null;
        }
        this.setState('register_failed');
        this.emit('registration_failed', r.code, r.reason ?? 'unknown');
        // 401/407 = auth issue (credentials wrong) — don't retry, it'll loop
        // forever spamming 3CX. All other codes (timeout, 5xx, 503, network) are
        // transient → retry with backoff.
        if (r.code !== 401 && r.code !== 407 && r.code !== 403) {
          this.scheduleSipReregister();
        }
        break;
      case 'unregistered':
        // 3CX told us we're no longer registered (TTL expired without renew, or
        // they restarted and forgot about us). Drop to a recoverable state and
        // re-register immediately — don't wait for backoff first attempt.
        // AMA çağrı sırasındaysa UI'ı (in_call) EZME — RTP registration'dan
        // bağımsız sürer; çağrı bitince recovery zaten re-register eder.
        if (inCall) {
          this.emit('log', 'unregistered during call — keeping call UI; will re-register after call');
          break;
        }
        console.warn('[sip] received unregistered event, forcing re-register');
        this.setState('idle');
        this.forceRegister();
        break;

      case 'calling':
        this.setState('calling');
        if (this.currentCall) this.currentCall.callId = r.call_id ?? this.currentCall.callId;
        break;
      case 'ringing':
      case 'proceeding':
        this.setState('ringing');
        break;

      case 'progress':
      case 'accepted':
      case 'updatingcall':
        // updatingcall = re-INVITE / SDP güncelleme. Offerless accept'te 3CX'in
        // answer'ı bu event'lerden biriyle gelir → handleRemoteJsep ile uygula.
        if (jsep) {
          this.sipHandle.handleRemoteJsep({
            jsep,
            success: () => this.emit('log', '  ✓ remote SDP applied'),
            error: (e: any) => this.emit('error', new Error('handleRemoteJsep: ' + e)),
          });
        }
        if ((r.event === 'accepted' || r.event === 'updatingcall') && this.currentCall && this.state !== 'in_call') {
          this.currentCall.answeredAt = this.currentCall.answeredAt ?? Date.now();
          this.emit('call', this.currentCall);
          this.setState('in_call');
        }
        break;

      case 'incomingcall': {
        const callId = r.call_id ?? `inc-${Date.now()}`;
        this.currentCall = {
          callId,
          direction: 'inbound',
          peer: this.parsePeerFromUri(r.username),
          displayName: r.displayname,
          startedAt: Date.now(),
        };
        this.pendingIncomingJsep = jsep ?? null;
        this.setState('ringing');
        this.emit('incoming', this.currentCall);
        this.emit('call', this.currentCall);
        // Do NOT auto-accept — UI shows modal, user clicks Accept → accept().
        break;
      }

      case 'hangup':
      case 'bye':
        this.setState('hangup');
        this.emit('hangup', r.reason || 'hangup');
        // Belt-and-suspenders: oncleanup track stop yapacak ama bazı Janus.js
        // sürümlerinde geç tetikleniyor — burada da çağırarak mic LED'i hemen söndür.
        this.stopAllMediaTracks('sip hangup event');
        this.currentCall = null;
        this.pendingIncomingJsep = null;
        this.emit('call', null);
        this.setState('registered');
        break;

      default:
        // ignore other events for now
        break;
    }
  }


  private onRemoteTrack(track: MediaStreamTrack, _mid: string, on: boolean) {
    console.log(`[remote-track] kind=${track.kind} on=${on} id=${track.id} muted=${track.muted} readyState=${track.readyState}`);
    if (track.kind !== 'audio') return;
    if (on) {
      if (!this.remoteStream) this.remoteStream = new MediaStream();
      this.remoteStream.addTrack(track);
      console.log(`[remote-track] stream tracks=${this.remoteStream.getTracks().length}, emitting to store`);
      this.emit('remoteStream', this.remoteStream);
    } else {
      this.emit('remoteStream', null);
      this.remoteStream = null;
    }
  }

  private parsePeerFromUri(uri: string | undefined): string {
    if (!uri) return '';
    const m = /sip:([^@]+)@/.exec(uri);
    return m ? m[1] : uri;
  }
}
